// SlideThumbnailRail.js
// PowerPoint-style vertical strip on the left of the player. Renders a real
// mini-preview of every slide (same renderSlideToCanvas the main player
// uses) with its number, and lets the user jump straight to any slide by
// clicking its thumbnail.

import React, { useRef, useEffect } from 'react';
import { renderSlideToCanvas } from '../canvas/SlideCanvasRenderer';
import './SlideThumbnailRail.css';

// Render full-res off-screen, then blit scaled down into the small visible
// canvas — same pattern SlidePlayer uses, so thumbnails look identical to
// the real slide regardless of what the renderer assumes about canvas size.
const RENDER_W = 1920;
const RENDER_H = 1080;
const THUMB_W = 480;
const THUMB_H = 270;

function SlideThumbnail({ slide, index, templateId, imageUrl, fontsLoaded, isActive, onSelect }) {
  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);
  const lastKeyRef = useRef('');

  useEffect(() => {
    if (!fontsLoaded || !slide || !canvasRef.current) return;

    const contentKey = JSON.stringify(slide.extractedContent || {});
    const key = `${templateId}|${imageUrl}|${contentKey}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    (async () => {
      try {
        if (!offscreenRef.current) {
          offscreenRef.current = document.createElement('canvas');
          offscreenRef.current.width = RENDER_W;
          offscreenRef.current.height = RENDER_H;
        }
        const offscreen = offscreenRef.current;

        await renderSlideToCanvas(offscreen, slide.extractedContent, templateId, imageUrl);

        // A newer render may have started while this one was in flight
        // (fast content edits, or React Strict Mode's dev-only double
        // effect invoke) — drop stale results using the key alone. This
        // check is already race-safe on its own: lastKeyRef is written
        // synchronously above, before the await, so a second invocation
        // triggered by the same render request sees the key already set
        // and returns early instead of duplicating work. We previously
        // also had a `cancelled` flag set in a cleanup function, but that
        // combination canceled the ONE run that was actually going to
        // finish, so no thumbnail ever drew. Don't reintroduce it.
        if (lastKeyRef.current !== key || !canvasRef.current) return;

        const ctx = canvasRef.current.getContext('2d', { alpha: false });
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.drawImage(offscreen, 0, 0, offscreen.width, offscreen.height, 0, 0, THUMB_W, THUMB_H);
      } catch (e) {
        console.error('SlideThumbnailRail render error', e);
      }
    })();
  }, [slide, templateId, imageUrl, fontsLoaded]);

  return (
    <button
      type="button"
      className={`thumb-card ${isActive ? 'thumb-card-active' : ''}`}
      onClick={onSelect}
      title={`Go to slide ${index + 1}`}
    >
      <span className="thumb-index">{index + 1}</span>
      <span className="thumb-canvas-wrap">
        <canvas ref={canvasRef} width={THUMB_W} height={THUMB_H} className="thumb-canvas" />
      </span>
    </button>
  );
}

export default function SlideThumbnailRail({
  processedSlides,
  slideTemplates,
  getSlideImageUrl,
  fontsLoaded,
  currentSlideIndex,
  onSelectSlide,
}) {
  const railRef = useRef(null);

  // Keep the active thumbnail in view when the active slide changes via
  // prev/next/autoplay, not just when a thumbnail itself is clicked.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const activeEl = rail.querySelector('.thumb-card-active');
    if (activeEl) activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentSlideIndex]);

  if (!processedSlides?.length) return null;

  return (
    <div className="thumb-rail" ref={railRef}>
      {processedSlides.map((slide, i) => (
        <SlideThumbnail
          key={i}
          slide={slide}
          index={i}
          templateId={slideTemplates[i] || 'modern_bullets_right'}
          imageUrl={getSlideImageUrl(i)}
          fontsLoaded={fontsLoaded}
          isActive={i === currentSlideIndex}
          onSelect={() => onSelectSlide(i)}
        />
      ))}
    </div>
  );
}